import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  useAssignUnAssignEntities
} from '../../../../hooks'
import useDebouncedValue from '../../../../hooks/useDebounceValue'
import { useSearchAccounts, useSearchAccountsMultiple } from '../../../../api/accounts'
import { MILLISECONDS_BEFORE_SEARCH } from '../../../../constants'
import { getSearchProp, mapAccountsMultiQueryResponse } from './helpers'

export const mapAccounts = (accounts) =>
  accounts.map(
    ({
      accountId,
      displayName,
      longName,
      shortName,
      shortNumber,
      custodian
    }) => ({
      id: accountId,
      shortNumber,
      name: displayName || longName || shortName || '',
      imageUrl: custodian ? custodian[0]?.imageUrl : ''
    })
  )

const defaultPaginationData = { take: 50, skip: 0 }
const defaultSearchPagination = { take: 10, skip: 0 }

const useClientAccounts = ({
  clientId,
  resetState = false,
  pagination = defaultPaginationData,
  searchPagination = defaultSearchPagination
}) => {
  const {
    searchResultOptions,
    onAssignUnAssignEntity,
    selectedEntities: selectedAccounts,
    assignedEntities: accountsAssigned,
    unAssignedEntities: accountsUnAssigned,
    setAssignedEntities: setAccountsAssigned,
    setUnAssignedEntities: setAccountsUnAssigned,
    persistEntitiesSelection: persistAccountsSelection
  } = useAssignUnAssignEntities({ resetState })

  const searchMultipleQuery = useMemo(() => {
    const paramsFilter = {
      assignedToClientIds: [clientId]
    }
    return {
      query: {
        assignedAccounts: {
          ...pagination,
          includes: {
            custodian: true
          },
          filters: paramsFilter
        },
        totalAssignedAccounts: {
          resultType: 'total',
          filters: paramsFilter
        }
      },
      queryOptions: {
        mapper: mapAccountsMultiQueryResponse,
        enabled: Boolean(clientId)
      }
    }
  }, [clientId, pagination])

  const {
    data: accountsData,
    refetch: refetchAccountsAssigned,
    isLoading: isLoadingAccountsAssigned
  } = useSearchAccountsMultiple(
    searchMultipleQuery.query,
    searchMultipleQuery.queryOptions
  )

  useEffect(() => {
    const { accounts = [], total } = accountsData || {}
    if (!isLoadingAccountsAssigned) {
      setAccountsAssigned({
        loading: false,
        data: mapAccounts(accounts),
        total: total
      })
    } else {
      setAccountsAssigned({
        loading: true
      })
    }
  }, [accountsData, isLoadingAccountsAssigned, setAccountsAssigned])

  const [query, setQuery] = useState('')
  const debouncedQuery = useDebouncedValue(query, MILLISECONDS_BEFORE_SEARCH)

  const searchAccountsQuery = useMemo(() => {
    const textSearch = [
      'longName',
      'shortName',
      'displayName',
      'shortNumber',
      'longNumber'
    ].reduce(
      (acc, key) => ({
        ...acc,
        [key]: getSearchProp(debouncedQuery)
      }),
      {}
    )
    return {
      ...searchPagination,
      textSearch,
      includes: {
        custodian: true
      },
      sort: [{ field: 'createdDate', dir: 'desc' }]
    }
  }, [searchPagination, debouncedQuery])

  const { isLoading, data } = useSearchAccounts(searchAccountsQuery)

  const isTransitioning = debouncedQuery !== query || isLoading

  useEffect(() => {
    const accounts = data?.data || []
    if (!isTransitioning) {
      setAccountsUnAssigned({
        loading: false,
        data: mapAccounts(accounts),
        total: accounts.length,
        isEmpty: !accounts.length
      })
    } else {
      setAccountsUnAssigned({
        loading: isTransitioning
      })
    }
  }, [data, isTransitioning, setAccountsUnAssigned])

  const onAssignUnAssignAccount = useCallback(
    ({ accountId, isAssign, account }) => {
      onAssignUnAssignEntity({
        entityId: accountId,
        entity: account,
        isAssign
      })
    },
    [onAssignUnAssignEntity]
  )

  return {
    accountsAssigned,
    accountsUnAssigned,
    selectedAccounts,
    searchResultOptions,
    refetchAccountsAssigned,
    onAssignUnAssignAccount,
    persistAccountsSelection,
    onSearchAccounts: setQuery
  }
}

export default useClientAccounts
